import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { RouterStateService } from '../../../../core/routing/router-state.service';
import { CataloguesService } from '../../../../core/services/catalogues.service';
import { LaunchDarklyService } from '../../../../core/services/launchdarkly.service';
import { FeatureService, PermissionService } from '../../../../core/services/permission';
import { UserType } from '../../../../core/services/permission/entities/user-type';
import { IntegrationProvider } from '../../../../data/models';
import { RepoSettingsService } from '../../../../data/repositories/repo-settings.service';
import { IntegrationGroup } from '../../../../pages/integrations-v2/integrations.enum';

@UntilDestroy()
@Component({
    selector: 'app-sidebar2-subpanel-integrations',
    templateUrl: './sidebar2-subpanel-integrations.component.html',
    standalone: false,
})
export class Sidebar2SubpanelIntegrationsComponent implements OnInit {
    options: any[] = [];
    // @ts-ignore TS2564
    @Input() heading: string;
    // @ts-ignore TS2564
    radioOption: string;
    toggleOpen = true;
    // @ts-ignore TS2564
    $connections: Observable<ApolloQueryResult<{ integrationProviders: { nodes: IntegrationProvider[] } }>>;

    preferredConnectionCount = 0;

    routerUrl: string = '';

    router = inject(Router);
    catalogueService = inject(CataloguesService);
    settings = inject(RepoSettingsService);
    #featureService = inject(FeatureService);
    #permissionService = inject(PermissionService);
    #routerStateService = inject(RouterStateService);
    #lds = inject(LaunchDarklyService);

    constructor() {
        this.routerUrl = this.#routerStateService.getCurrentUrlRoute();
        this.#routerStateService.routeChanged$.pipe(untilDestroyed(this)).subscribe(() => {
            this.routerUrl = this.#routerStateService.getCurrentUrlRoute();
        });
    }

    ngOnInit() {
        this.#featureService.userType$
            .pipe(
                untilDestroyed(this),
                mergeMap((userType) => {
                    this.$connections = this.catalogueService.getIntegrationProviders(
                        this.settings.HideInternalSupplier,
                        true,
                        userType === 'Supplier' ? IntegrationGroup.Manufacturer : IntegrationGroup.Supplier
                    );

                    return of(userType);
                }),
                mergeMap((userType) => {
                    if (this.#permissionService.isAvailable('account.maintain-integrations.view-connections')) {
                        return this.catalogueService.watchGetPreferredOutgoingIntegrationConnections(userType);
                    } else return EMPTY;
                })
            )
            .subscribe((res) => {
                this.preferredConnectionCount = res?.data?.outgoingIntegrationConnections?.nodes?.length ?? 0;
            });

        const savedRoute = this.settings.NavigationV2IntegrationsV2RouteChoice || this.#routerStateService.getCurrentUrlRoute();
        switch (savedRoute) {
            case '/integrations/integrations-list':
                this.radioOption = 'suppliers';
                break;
            case '/account/setup/integrations':
                this.radioOption = 'appint';
                break;
            case '/integrations/buildxact-add-ons':
                this.radioOption = 'add-ons';
                break;
            case '/integrations/partners':
                this.radioOption = 'partners';
                break;
        }

        this.#featureService.userType$.pipe(untilDestroyed(this)).subscribe((userType) => {
            this.setupIntegrations(userType);
        });
    }

    private setupIntegrations(userType: UserType) {
        if (this.#featureService.isStateAvailable('account.integrations.applications')) {
            if (!this.options.find((o) => o.label === 'Supplier Integrations')) {
                this.options.push({
                    label: 'Application Integrations',
                    route: ['/account', 'setup', 'integrations'],
                    subRoute: 'appint',
                    count: 5,
                });
            }
        }

        if (
            this.#lds.get('integrations-addon-marketplace') &&
            this.#featureService.isStateAvailable('account.integrations.addons') &&
            userType === UserType.Client
        ) {
            if (!this.options.find((o) => o.label === 'Buildxact Add-ons')) {
                this.options.push({
                    label: 'Buildxact Add-ons',
                    route: ['/integrations', 'buildxact-add-ons'],
                    subRoute: 'add-ons',
                    count: null,
                });
            }
        }

        if (
            this.#featureService.isStateAvailable('account.integrations.suppliers') &&
            this.#permissionService.isAvailable('account.maintain-integrations.view-integrations') &&
            this.#permissionService.isAvailable('account.maintain-integrations.view-connections') &&
            !this.#featureService.isAvailable('manufacturer')
        ) {
            if (!this.options.find((o) => o.label === 'Supplier Integrations')) {
                this.options.push({
                    label: 'Supplier Integrations',
                    route: ['/integrations/integrations-list'],
                    subRoute: 'suppliers',
                    count: null,
                });
            }
        }
    }

    // @ts-ignore TS7006
    radioSelection(item) {
        let settingsOption: string;
        switch (item.subRoute) {
            case 'suppliers':
                settingsOption = 'NavigationV2IntegrationsV2';
                break;
            case 'appint':
                settingsOption = 'NavigationV2Integrations';
                break;
            case 'add-ons':
                settingsOption = 'NavigationV2IntegrationsAddOns';
                break;
            case 'partners':
                settingsOption = 'NavigationV2IntegrationsPartners';
                break;
            default:
                return;
        }

        const queryParams = JSON.parse(this.settings[`${settingsOption}Params`]);
        this.router.navigate(item.route, { queryParams, queryParamsHandling: 'merge' });

        this.settings.NavigationV2IntegrationsV2RouteChoice = this.settings[`${settingsOption}RoutePath`];
    }
}
